import { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Platform, StyleSheet, Text, View } from 'react-native';
import { AnimatedLogoIcon } from './AnimatedLogoIcon';
import { colors } from '../styles/theme';

const captions = [
  'Warming up the flux capacitor…',
  'Consulting the Sorting Hat…',
  'Checking for main-character energy…',
  'Untangling your plot twists…',
  'Asking the universe for a second opinion…',
];

export function SoulThreadLoader({
  label = 'Finding your thread',
  size = 200,
}: {
  label?: string;
  size?: number;
}) {
  const [index, setIndex] = useState(0);
  const [reduced, setReduced] = useState(true);
  const opacity = useRef(new Animated.Value(1)).current;
  const offset = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    let active = true;
    void AccessibilityInfo.isReduceMotionEnabled().then((value) => {
      if (active) setReduced(value);
    });
    const listener = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduced);
    return () => {
      active = false;
      listener.remove();
    };
  }, []);
  useEffect(() => {
    let active = true;
    let animation: Animated.CompositeAnimation | undefined;
    const timer = setInterval(() => {
      if (reduced) {
        setIndex((value) => (value + 1) % captions.length);
        return;
      }
      const timing = (value: Animated.Value, toValue: number, duration: number) =>
        Animated.timing(value, {
          toValue,
          duration,
          useNativeDriver: Platform.OS !== 'web',
        });
      animation = Animated.parallel([timing(opacity, 0, 220), timing(offset, -5, 220)]);
      animation.start(({ finished }) => {
        if (!active || !finished) return;
        setIndex((value) => (value + 1) % captions.length);
        offset.setValue(5);
        animation = Animated.parallel([timing(opacity, 1, 320), timing(offset, 0, 320)]);
        animation.start();
      });
    }, 4800);
    return () => {
      active = false;
      clearInterval(timer);
      animation?.stop();
      opacity.setValue(1);
      offset.setValue(0);
    };
  }, [reduced, opacity, offset]);
  return (
    <View
      style={styles.container}
      accessibilityRole="progressbar"
      accessibilityLabel={label}
      testID="soulthread-loader"
    >
      <AnimatedLogoIcon size={size} showMatrix />
      <Text style={styles.title}>{label}</Text>
      <Animated.View style={{ opacity, transform: [{ translateY: offset }] }}>
        <Text style={styles.caption}>{captions[index]}</Text>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { alignItems: 'center', justifyContent: 'center', gap: 24, padding: 24 },
  title: { color: colors.text, fontSize: 24, fontWeight: '500', textAlign: 'center' },
  caption: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
    minHeight: 44,
    maxWidth: 340,
  },
});
